import { useEffect, useState } from 'react'

interface LiveCopies {
  copiesLeft: number
  claimed: number
  totalCopies: number
  claimPulse: number
}

/**
 * Simulates copies being claimed in real time: every few seconds one more copy
 * is taken, until only `minLeft` remain. `claimPulse` increments on each claim
 * so the UI can flash / re-animate its numbers.
 */
export function useLiveCopies(
  initialLeft: number,
  totalCopies = 100,
  minLeft = 3
): LiveCopies {
  const [copiesLeft, setCopiesLeft] = useState(initialLeft)
  const [claimPulse, setClaimPulse] = useState(0)

  useEffect(() => {
    setCopiesLeft(initialLeft)
    setClaimPulse(0)
  }, [initialLeft])

  useEffect(() => {
    if (copiesLeft <= minLeft) return
    const delay = 6500 + Math.random() * 8500
    const timer = window.setTimeout(() => {
      setCopiesLeft(current => Math.max(minLeft, current - 1))
      setClaimPulse(pulse => pulse + 1)
    }, delay)
    return () => window.clearTimeout(timer)
  }, [copiesLeft, minLeft])

  return {
    copiesLeft,
    claimed: Math.max(0, Math.min(totalCopies, totalCopies - copiesLeft)),
    totalCopies,
    claimPulse
  }
}
